import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import { cx } from '@/lib/cx';
import styles from './CodePicker.module.css';

/**
 * A labelled multi-select of codes — the airports and airlines of a flight access.
 *
 * Chips for what is chosen, a search box after them, and a list under it. Not the
 * board's column filter: that one lives in a table header, reads its codes as one
 * comma-separated summary and writes to the URL. This is a form field — what is
 * chosen has to stay readable as separate things, each with its name, each removable
 * on its own, because an administrator is deciding what somebody else will be allowed
 * to see.
 *
 * The caller owns the query and the options, ranked and capped: an airport is asked of
 * the server as it is typed, an airline is matched in the browser, and this component
 * has no opinion about which.
 *
 * **The list is portalled**, into the dialog the field is in, or the body. Into the
 * body alone would put it under a modal's top layer, and the browser makes everything
 * outside an open modal inert; rendered in place it would be clipped by the scrolling
 * panel it sits in.
 */

export interface PickerOption {
  /** What gets chosen. */
  code: string;
  /** The human half: an airport's city, an airline's name. */
  label: string;
  /** Distinguishes two options that share a code, which dictionaries do. */
  key?: string | number | undefined;
  /** A quiet remark after the label — why this option is not like the others. */
  note?: string | undefined;
}

interface CodePickerProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  values: readonly string[];
  onChange: (values: string[]) => void;
  query: string;
  onQueryChange: (query: string) => void;
  options: readonly PickerOption[];
  isLoading?: boolean | undefined;
  /** Shown in place of options when there are none and nothing is loading. */
  emptyLabel: string;
  /** A quiet line under the list: what was cut off, or that a search is running. */
  footnote?: string | null | undefined;
  /** The name a chip shows beside its code. Empty when nothing is known. */
  nameOf: (code: string) => string;
  placeholder: string;
  disabled?: boolean | undefined;
}

/** The list's tallest — the CSS fallback says the same, `16rem`. */
const MENU_MAX = 256;
/** Below this much room under the box, a list that would fit better above goes above. */
const MENU_MIN = 160;
const GAP = 4;

interface Placement {
  /** The list hangs from `top`, or stands on `bottom` when it opens upwards. */
  edge: { top: number } | { bottom: number };
  left: number;
  width: number;
  maxHeight: number;
}

/**
 * Where the list goes for a box at `rect`: under it, unless the box is near the bottom
 * of the window and there is more room above — a field at the foot of the panel would
 * otherwise open a list that runs off the screen.
 */
const placementFor = (rect: DOMRect): Placement => {
  const below = window.innerHeight - rect.bottom - GAP * 2;
  const above = rect.top - GAP * 2;
  const upwards = below < MENU_MIN && above > below;

  return {
    edge: upwards ? { bottom: window.innerHeight - rect.top + GAP } : { top: rect.bottom + GAP },
    left: rect.left,
    width: rect.width,
    maxHeight: Math.min(MENU_MAX, upwards ? above : below),
  };
};

export const CodePicker = ({
  label,
  hint,
  error,
  values,
  onChange,
  query,
  onQueryChange,
  options,
  isLoading = false,
  emptyLabel,
  footnote = null,
  nameOf,
  placeholder,
  disabled = false,
}: CodePickerProps) => {
  const { t } = useTranslation();

  const id = useId();
  const listId = `${id}-list`;
  const messageId = `${id}-message`;
  const message = error ?? hint;

  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  /** Where to paint the list, in viewport coordinates, and what to paint it into. */
  const [place, setPlace] = useState<(Placement & { host: HTMLElement }) | null>(null);

  const measure = () => {
    const box = boxRef.current;
    if (!box) return;

    setPlace({ ...placementFor(box.getBoundingClientRect()), host: box.closest('dialog') ?? document.body });
  };

  const open = () => {
    if (disabled) return;
    measure();
    setIsOpen(true);
  };

  /**
   * Closes the list, and drops whatever was being searched for.
   *
   * A half-typed search left in the box after the list has gone reads as a value — as
   * though `ast` had been chosen alongside the chips — when nothing about it was.
   */
  const close = () => {
    setIsOpen(false);
    setHighlight(-1);
    if (query !== '') onQueryChange('');
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    // Kept in step rather than closed on scroll: the panel scrolls, the list itself
    // scrolls, and closing on either would shut it under the reader's hand.
    const reposition = () => {
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPlace((current) => (current ? { ...placementFor(rect), host: current.host } : current));
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (boxRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
      setHighlight(-1);
    };

    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    window.addEventListener('resize', reposition);
    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      window.removeEventListener('scroll', reposition, { capture: true });
      window.removeEventListener('resize', reposition);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isOpen]);

  // The highlighted option is kept in view, so arrowing past the bottom of a long list
  // does not move a highlight the reader can no longer see.
  useEffect(() => {
    if (!isOpen || highlight < 0) return;
    document.getElementById(`${listId}-${highlight}`)?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, highlight, listId]);

  const toggle = (code: string) => {
    const upper = code.toUpperCase();
    const chosen = values.includes(upper);

    onChange(chosen ? values.filter((value) => value !== upper) : [...values, upper]);

    // A pick ends the search that found it, so the next word typed is a new search
    // rather than an edit of the last one. The list stays open for the next pick.
    if (!chosen && query !== '') {
      onQueryChange('');
      setHighlight(-1);
    }

    inputRef.current?.focus();
  };

  const remove = (code: string) => {
    onChange(values.filter((value) => value !== code));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Opening with the keyboard is how this is reachable without a mouse.
      event.preventDefault();
      if (!isOpen) {
        open();
        setHighlight(0);
        return;
      }

      const count = options.length;
      if (count === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlight((current) => (current + step + count) % count);
      return;
    }

    if (event.key === 'Enter' && isOpen) {
      // Never a submit while the list is open: Enter here means "this one", and a form
      // sent from the middle of a search would save a selection nobody finished.
      event.preventDefault();
      const chosen = options[highlight] ?? (options.length === 1 ? options[0] : undefined);
      if (chosen) toggle(chosen.code);
      return;
    }

    if (event.key === 'Escape' && isOpen) {
      // Closes the list and nothing else — in the create dialog the same key would
      // otherwise close the dialog, and everything typed into it with it.
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }

    // Backspace in an empty box takes the last chip, as every tag input does.
    if (event.key === 'Backspace' && query === '' && values.length > 0) {
      const last = values[values.length - 1];
      if (last !== undefined) remove(last);
    }
  };

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>

      {/* A click anywhere in the box is a click on the search: the chips are inside it,
          and aiming at the few pixels of input left beside them is not the task. */}
      <div
        ref={boxRef}
        className={cx(styles.box, error && styles.invalid, disabled && styles.disabled)}
        onClick={() => inputRef.current?.focus()}
      >
        {values.map((code) => {
          const name = nameOf(code);

          return (
            <span key={code} className={styles.chip} title={name || undefined}>
              <span className={styles.chipCode}>{code}</span>
              {name !== '' && <span className={styles.chipName}>{name}</span>}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={(event) => {
                  // Not a click on the box: removing a chip should not open the list.
                  event.stopPropagation();
                  remove(code);
                }}
                aria-label={t('users.access.remove', { code })}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          );
        })}

        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          className={styles.input}
          value={query}
          placeholder={values.length === 0 ? placeholder : ''}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          aria-expanded={isOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={isOpen && highlight >= 0 ? `${listId}-${highlight}` : undefined}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.value;
            onQueryChange(next);
            // The first match is ready for Enter; an empty box highlights nothing, so
            // Enter on the full list does not pick whichever row sorts first.
            setHighlight(next.trim() === '' ? -1 : 0);
            open();
          }}
          onFocus={open}
          onClick={open}
          onBlur={(event) => {
            // A click on an option keeps the focus here (the list prevents the
            // mousedown), so leaving is only ever leaving.
            if (menuRef.current?.contains(event.relatedTarget as Node | null)) return;
            close();
          }}
          onKeyDown={handleKeyDown}
        />
      </div>

      {message && (
        <span id={messageId} className={cx(styles.message, error && styles.messageError)}>
          {message}
        </span>
      )}

      {isOpen &&
        place !== null &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-label={label}
            aria-multiselectable
            className={styles.menu}
            style={{
              ...place.edge,
              left: place.left,
              width: Math.max(place.width, 260),
              maxHeight: place.maxHeight,
            }}
            // Keeps the focus in the search while an option is clicked, so the blur
            // that would close the list never happens.
            onMouseDown={(event) => event.preventDefault()}
          >
            {options.length === 0 ? (
              <p className={styles.menuNote}>{isLoading ? t('common.loading') : emptyLabel}</p>
            ) : (
              options.map((option, index) => {
                const ticked = values.includes(option.code.toUpperCase());

                return (
                  <button
                    key={option.key ?? option.code}
                    id={`${listId}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={ticked}
                    tabIndex={-1}
                    className={cx(styles.option, index === highlight && styles.active)}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => toggle(option.code)}
                  >
                    <span className={cx(styles.check, ticked && styles.checked)} aria-hidden="true">
                      {ticked && <CheckIcon />}
                    </span>
                    <span className={styles.code}>{option.code}</span>
                    <span className={styles.name}>{option.label}</span>
                    {option.note && <span className={styles.note}>{option.note}</span>}
                  </button>
                );
              })
            )}

            {footnote !== null && options.length > 0 && <p className={styles.menuNote}>{footnote}</p>}
          </div>,
          place.host,
        )}
    </div>
  );
};
